import { useEffect, useRef } from 'react';

import { useDisclosure } from '@mantine/hooks';

import { Outlet, createFileRoute, useRouter } from '@tanstack/react-router';

import { AppShell } from '@optiro/ui';

import { AlertProvider } from '@/components/Alert';
import { AppHeaderOrchestrator } from '@/components/AppHeader';
import { TermsAndConditions } from '@/components/Consents/TermsAndConditions';
import { InteractiveLoader } from '@/components/InteractiveLoader';
import { IntervalDrawer, IntervalDrawerProvider } from '@/components/IntervalDrawer';
import CreateProject from '@/components/Modals/CreateProject/CreateProject';
import SideBar from '@/components/SideBar';
import { TaskDrawer } from '@/components/TaskDrawer/TaskDrawer';
import { TrialWelcomeModal } from '@/components/TrialWelcomeModal/TrialWelcomeModal';
import { useIntervalCommentsDrawerStore } from '@/hooks/intervalComments/useIntervalCommentsDrawerStore';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { BookingCommentsDrawer } from '@/modules/Samarbejde/BookingCommentsDrawer';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import { useTaskDrawerStore } from '@/stores/useTaskDrawerStore';

export const Route = createFileRoute('/app')({
  component: AppLayout,
});

export default function AppLayout() {
  const router = useRouter();
  const { userData, isSignedIn, loading } = useAuth();

  // Check if on specific route

  // Handle authentication redirect
  const redirectTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (loading) return;

    if (!isSignedIn) {
      // Wait 300ms before redirecting
      redirectTimeout.current = setTimeout(() => {
        router.navigate({
          to: '/experiences/login',
          search: { notice: 'login-to-continue' },
        });
      }, 300);
    }

    return () => {
      if (redirectTimeout.current) clearTimeout(redirectTimeout.current);
    };
  }, [loading, isSignedIn, router, userData]);

  // Global keyboard shortcuts for navigation and create project
  useKeyboardShortcuts({
    onCreateProject: () => {
      const createProjectElement = document.querySelector(
        '[data-testid="create-project-button"]'
      ) as HTMLButtonElement;
      if (createProjectElement) {
        createProjectElement.click();
      }
    },
  });

  const [opened, { toggle }] = useDisclosure();
  const globalTaskId = useTaskDrawerStore((s) => s.taskId);
  const globalTaskInitialTab = useTaskDrawerStore((s) => s.initialTab);
  const closeGlobalTaskDrawer = useTaskDrawerStore((s) => s.close);
  const globalCommentsIntervalId = useIntervalCommentsDrawerStore((s) => s.intervalId);
  const globalCommentsTaskName = useIntervalCommentsDrawerStore((s) => s.taskName);
  const closeGlobalCommentsDrawer = useIntervalCommentsDrawerStore((s) => s.close);

  if (loading) {
    return (
      <InteractiveLoader
        message="Forbereder din arbejdsplads"
        rotatingMessages={[
          'Indlæser dine projekter...',
          'Opdaterer din kalender...',
          'Henter de seneste data...',
          'Næsten klar...',
        ]}
      />
    );
  }

  // Return null while redirecting to prevent rendering
  if (!isSignedIn && !loading) {
    return null;
  }

  return (
    <AlertProvider>
      <TrialWelcomeModal />
      <IntervalDrawerProvider>
        <AppShell
          layout="alt"
          header={{ height: 51 }}
          navbar={{
            width: 250,
            breakpoint: 'sm',
            collapsed: { mobile: !opened },
          }}
        >
          <AppHeaderOrchestrator opened={opened} onToggle={toggle} />

          <AppShell.Navbar>
            <SideBar opened={opened} handleToggle={toggle} />
          </AppShell.Navbar>

          <AppShell.Main className={'bg-background'}>
            <Outlet />
          </AppShell.Main>

          {userData?.terms_and_conditions_accepted === false && (
            <TermsAndConditions open={true} accountId={userData.id} />
          )}

          {/* Hidden CreateProject for global keyboard shortcut accessibility */}
          <div style={{ display: 'none' }}>
            <CreateProject />
          </div>

          <IntervalDrawer />

          {globalTaskId !== null && (
            <TaskDrawer
              opened
              taskId={globalTaskId}
              initialTab={globalTaskInitialTab}
              onClose={closeGlobalTaskDrawer}
            />
          )}

          <BookingCommentsDrawer
            intervalId={globalCommentsIntervalId}
            taskName={globalCommentsTaskName}
            onClose={closeGlobalCommentsDrawer}
            withOverlay={false}
          />

          {/* <Assistant /> */}
        </AppShell>
      </IntervalDrawerProvider>
    </AlertProvider>
  );
}
